import { useQuery } from "@tanstack/react-query";

/**
 * Hook to fetch applications with server-side pagination, filtering, sorting, and search
 * Uses the new /api/applications/query endpoint
 * 
 * @param {Object} params - Query parameters
 * @param {number} params.page - Current page number (1-based)
 * @param {number} params.pageSize - Number of items per page (default: 20)
 * @param {Object} params.filters - Filter options
 * @param {string[]} params.filters.status - Status filter (e.g., ["Applied", "Interview"])
 * @param {string[]} params.filters.cities - City filter
 * @param {string[]} params.filters.companies - Company filter
 * @param {boolean} params.filters.includeArchived - Include archived applications
 * @param {boolean} params.filters.archivedOnly - Show only archived applications
 * @param {Object} params.sorting - Sorting options
 * @param {string} params.sorting.sortBy - Field to sort by
 * @param {string} params.sorting.sortOrder - Sort order (asc/desc)
 * @param {Object} params.search - Search options
 * @param {string} params.search.query - Search query text
 * @param {string[]} params.search.fields - Fields to search in
 * @returns {Object} React Query result with { data, pagination, appliedFilters }
 */
export const useApplicationsQuery = ({
  page = 1,
  pageSize = 20,
  filters = {},
  sorting = { sortBy: "applicationDate", sortOrder: "desc" },
  search = null,
  enabled = true,
} = {}) => {
  const token = localStorage.getItem("token");

  return useQuery({
    queryKey: ["applications", "query", { page, pageSize, filters, sorting, search }],
    queryFn: async () => {
      if (!token) {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            pageSize: 20,
            totalCount: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: false,
            firstItemIndex: 0,
            lastItemIndex: 0,
          },
          appliedFilters: null,
        };
      }

      // Build request payload
      const payload = {
        pagination: {
          page,
          pageSize,
        },
        sorting: {
          sortBy: sorting.sortBy || "applicationDate",
          sortOrder: sorting.sortOrder || "desc",
        },
      };

      // Add filters if provided
      if (filters && Object.keys(filters).length > 0) {
        payload.filters = buildFilters(filters);
      }

      // Add search if provided
      if (search && search.query) {
        payload.search = {
          query: search.query,
          fields: search.fields || undefined,
          caseSensitive: search.caseSensitive || false,
        };
      }

      const response = await fetch("/api/applications/query", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch applications: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      // Return the server response directly
      // Expected format: { data: ApplicationDto[], pagination: PaginationMetadata, appliedFilters: AppliedFiltersSummary }
      return result;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!token && enabled,
    keepPreviousData: true, // CRITICAL: Prevents loading flicker during filter/page changes
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

/**
 * Build filters object from UI state
 * Converts client filter state to API filter format
 */
function buildFilters(filters) {
  const apiFilters = {};

  // Status filter
  if (filters.status && filters.status.length > 0) {
    apiFilters.status = Array.isArray(filters.status) ? filters.status : [filters.status];
  }

  // Location filters
  if (filters.cities && filters.cities.length > 0) {
    apiFilters.cities = Array.isArray(filters.cities) ? filters.cities : [filters.cities];
  }

  if (filters.states && filters.states.length > 0) {
    apiFilters.states = Array.isArray(filters.states) ? filters.states : [filters.states];
  }

  // Company filter
  if (filters.companies && filters.companies.length > 0) {
    apiFilters.companies = Array.isArray(filters.companies) ? filters.companies : [filters.companies];
  }

  // Archived filter
  if (filters.includeArchived !== undefined) {
    apiFilters.includeArchived = filters.includeArchived;
  }

  if (filters.archivedOnly !== undefined) {
    apiFilters.archivedOnly = filters.archivedOnly;
  }

  // Remote only
  if (filters.remoteOnly !== undefined) {
    apiFilters.remoteOnly = filters.remoteOnly;
  }

  // Match score range
  if (filters.minMatchScore !== undefined) {
    apiFilters.minMatchScore = filters.minMatchScore;
  }

  if (filters.maxMatchScore !== undefined) {
    apiFilters.maxMatchScore = filters.maxMatchScore;
  }

  // Date ranges
  if (filters.applicationDateFrom) {
    apiFilters.applicationDateFrom = filters.applicationDateFrom;
  }

  if (filters.applicationDateTo) {
    apiFilters.applicationDateTo = filters.applicationDateTo;
  }

  return apiFilters;
}

/**
 * Hook to get application stats for Dashboard
 * Reuses the query endpoint with pageSize: 1 to get total count
 */
export const useApplicationStats = () => {
  return useApplicationsQuery({
    page: 1,
    pageSize: 1,
    filters: { includeArchived: false }, // Count only active apps
  });
};
